import { useEffect, useMemo, useState, useCallback } from "react";
import { Table, Button, Input, Space, Tooltip } from "antd";
import { DeleteOutlined, SearchOutlined, CloseCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import config from "../../config";
import { ToastContainer, toast, Bounce } from "react-toastify";
import SelectUsersMiniTable from "../Commons/SelectUsersMiniTable";
import { FaSpinner } from "react-icons/fa";
import {
    sortByOrder,
    toExamBankItemDtos,
    getNextOrder,
    validateOrderedItems,
    normalizeBankItemOrder,
} from "../../utils/examBankOrder";
import QrCodeField from "../Commons/QrCodeField";
import {
    getApiErrorMessage,
    isQrCodeConflict,
    normalizeQrCode,
    withOptionalQrCode,
} from "../../utils/qrCode";
import VersionConflictModal from "../Commons/VersionConflictModal";
import {
    extractVersion,
    getVersionConflictMessage,
    isVersionConflict,
} from "../../utils/versionConflict";

const buildTitleFilter = (title) => (title ? `title=*${title}/i` : "");

const ClearSuffix = ({ onClick, style }) => (
    <Tooltip title="پاک‌کردن">
        <CloseCircleOutlined
            onClick={onClick}
            style={{ cursor: "pointer", ...style, textAlign: "left" }}
            role="button"
            aria-label="clear"
            tabIndex={0}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick()}
        />
    </Tooltip>
);

const extractUserId = (user) => {
    if (user == null) return null;
    if (typeof user === "string" || typeof user === "number") return String(user);
    return (
        user.userID ??
        user.UserID ??
        user.userId ??
        user.UserId ??
        user.user_id ??
        user.id ??
        user.Id ??
        user.user?.userID ??
        user.user?.id ??
        null
    );
};

const isHydratedUser = (user) =>
    user &&
    typeof user === "object" &&
    (user.username != null ||
        user.lastname != null ||
        user.firstname != null ||
        user.personalId != null ||
        user.phoneNumber != null);

const normalizeUserRecord = (user) => {
    const userID = extractUserId(user);
    if (!userID) return null;
    if (typeof user === "string" || typeof user === "number") {
        return { userID: String(user) };
    }
    return { ...user, userID };
};

/** Resolve bank users into SelectUsersMiniTable-ready records (must have userID). */
const collectBankUserRefs = (bank) => {
    if (!bank || typeof bank !== "object") {
        return { rawUsers: [], rawIds: [] };
    }

    const rawUsers = [
        bank.users,
        bank.Users,
        bank.assignedUsers,
        bank.AssignedUsers,
        bank.examBankUsers,
        bank.ExamBankUsers,
    ].find(Array.isArray) || [];

    const rawIds = [
        bank.userIds,
        bank.UserIds,
        bank.userIDs,
        bank.UserIDs,
        bank.assignedUserIds,
        bank.AssignedUserIds,
    ].find(Array.isArray) || [];

    return { rawUsers, rawIds };
};

const bankHasUserRefs = (bank) => {
    const { rawUsers, rawIds } = collectBankUserRefs(bank);
    return rawUsers.length > 0 || rawIds.length > 0;
};

const resolveBankUsers = async (bank) => {
    const { rawUsers, rawIds } = collectBankUserRefs(bank);

    const ids = [
        ...new Set(
            [...rawUsers, ...rawIds]
                .map(extractUserId)
                .filter(Boolean)
                .map(String)
        ),
    ];

    const hydratedFromPayload = rawUsers
        .filter(isHydratedUser)
        .map(normalizeUserRecord)
        .filter(Boolean);

    if (ids.length === 0) return hydratedFromPayload;

    if (hydratedFromPayload.length >= ids.length) {
        return hydratedFromPayload;
    }

    try {
        const res = await axios.get(`${config.apiBaseUrl}/api/users/getbyids`, {
            params: { ids: ids.join(",") },
            withCredentials: true,
            headers: { HeaderKey: config.HeaderKey },
        });
        const payload = res.data;
        const fetched = Array.isArray(payload)
            ? payload
            : Array.isArray(payload?.users)
                ? payload.users
                : Array.isArray(payload?.data)
                    ? payload.data
                    : [];
        if (fetched.length > 0) {
            return fetched.map(normalizeUserRecord).filter(Boolean);
        }
    } catch {
        // fall through to GetUsersList filter
    }

    try {
        const filter = ids.map((id) => `(userID=${id})`).join("|");
        const { data } = await axios.get(
            `${config.apiBaseUrl}/api/Users/GetUsersList`,
            {
                params: { page: 1, pageSize: Math.max(ids.length, 50), filter },
                headers: { HeaderKey: config.HeaderKey },
                withCredentials: true,
            }
        );
        const fetched = data?.users || [];
        if (fetched.length > 0) {
            return fetched.map(normalizeUserRecord).filter(Boolean);
        }
    } catch {
        // keep whatever we have
    }

    return hydratedFromPayload.length > 0
        ? hydratedFromPayload
        : ids.map((id) => ({ userID: id }));
};

const ExamBankForm = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const initialBank = location.state?.initialBank || null;
    const parentBank = location.state?.parentBank || null;

    const [title, setTitle] = useState("");
    const [qrCode, setQrCode] = useState("");
    const [qrCodeError, setQrCodeError] = useState("");
    const [version, setVersion] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isOutdated, setIsOutdated] = useState(false);
    const [versionConflict, setVersionConflict] = useState({
        open: false,
        message: "",
    });
    const [isRefreshing, setIsRefreshing] = useState(false);

    const [allExams, setAllExams] = useState([]);
    const [allBanks, setAllBanks] = useState([]);
    const [examTotal, setExamTotal] = useState(0);
    const [bankTotal, setBankTotal] = useState(0);

    const [selectedExams, setSelectedExams] = useState([]);
    const [selectedChildBanks, setSelectedChildBanks] = useState([]);
    const [selectedParentBanks, setSelectedParentBanks] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    
    // Separate pagination state for each table
    const [examsPagination, setExamsPagination] = useState({ current: 1, pageSize: 6 });
    const [banksPagination, setBanksPagination] = useState({ current: 1, pageSize: 6 });
    const [examsSearchFilters, setExamsSearchFilters] = useState({ title: "" });
    const [banksSearchFilters, setBanksSearchFilters] = useState({ title: "" });

    const selectedExamIds = useMemo(() => selectedExams.map((e) => e.id), [selectedExams]);
    const selectedChildBankIds = useMemo(() => selectedChildBanks.map((b) => b.id), [selectedChildBanks]);
    const selectedParentBankIds = useMemo(() => selectedParentBanks.map((b) => b.id), [selectedParentBanks]);

    const updateSelectedExams = (next) => setSelectedExams(sortByOrder(next));
    const updateSelectedChildBanks = (next) => setSelectedChildBanks(sortByOrder(next));


    const isSelf = useCallback(
        (id) => initialBank?.id && id === initialBank.id,
        [initialBank]
    );

    const handleExamsSearchChange = (e, field) => {
        const value = e?.target ? e.target.value : e;
        setExamsSearchFilters((prev) => ({ ...prev, [field]: value }));
    };

    const handleBanksSearchChange = (e, field) => {
        const value = e?.target ? e.target.value : e;
        setBanksSearchFilters((prev) => ({ ...prev, [field]: value }));
    };

    // Fetch exams with pagination
    const fetchExams = useCallback(async (page, pageSize, titleFilter = "") => {
        try {
            const requestBody = {
                Page: page,
                PageSize: pageSize,
                Filter: buildTitleFilter(titleFilter),
                OrderBy: "",
            };
            const examsRes = await axios.post(
                `${config.apiBaseUrl}/api/Exam/GetList`,
                requestBody,
                {
                    headers: {
                        "Content-Type": "application/json",
                        HeaderKey: config.HeaderKey,
                    },
                    withCredentials: true,
                }
            );

            const examsData = examsRes?.data?.data || [];

            setAllExams(
                examsData.map((e) => ({
                    ...e,
                    key: e.id,
                    title: e.title ?? e.name ?? e.id,
                    qCount: e.examQuestions?.length || 0
                }))
            );

            setExamTotal(examsRes?.data?.count ?? examsData.length);
        } catch (err) {
            toast.error("خطا در بارگذاری آزمون‌ها");
            setAllExams([]);
            setExamTotal(0);
        }
    }, []);

    // Fetch banks with pagination
    const fetchBanks = useCallback(async (page, pageSize, titleFilter = "") => {
        try {
            const requestBody = {
                Page: page,
                PageSize: pageSize,
                Filter: buildTitleFilter(titleFilter),
                OrderBy: "",
            };
            const banksRes = await axios.post(
                `${config.apiBaseUrl}/api/ExamBank/GetList`,
                requestBody,
                {
                    headers: {
                        "Content-Type": "application/json",
                        HeaderKey: config.HeaderKey,
                    },
                    withCredentials: true,
                }
            );

            const banksData = banksRes?.data?.data?.data || [];
            const totalCount = banksRes?.data?.data?.count || 0;

            const filteredBanks = (banksData || []).filter((b) => !isSelf(b.id));
            setAllBanks(
                filteredBanks.map((b) => ({
                    ...b,
                    key: b.id,
                    title: b.title ?? b.name ?? b.id,
                    examsCount: b.exams?.length || 0
                }))
            );

            // Calculate total, accounting for filtered self bank
            const selfBankExists = banksData.some(b => isSelf(b.id));
            setBankTotal(selfBankExists ? totalCount - 1 : totalCount);
        } catch (err) {
            toast.error("خطا در بارگذاری بانک‌ها");
            setAllBanks([]);
            setBankTotal(0);
        }
    }, [isSelf, initialBank?.id]);

    const fetchExamsWithFilters = (override = {}) => {
        const effectiveFilters = { ...examsSearchFilters, ...override };
        setExamsPagination((prev) => ({ ...prev, current: 1 }));
        fetchExams(1, examsPagination.pageSize, effectiveFilters.title);
    };

    const fetchBanksWithFilters = (override = {}) => {
        const effectiveFilters = { ...banksSearchFilters, ...override };
        setBanksPagination((prev) => ({ ...prev, current: 1 }));
        fetchBanks(1, banksPagination.pageSize, effectiveFilters.title);
    };

    const mapBankPayload = useCallback((bank, bankId) => {
        if (!bank) return null;
        const entity = bank.examBank ?? bank.ExamBank ?? bank;
        const { rawUsers, rawIds } = collectBankUserRefs(entity);
        const fallbackRefs = collectBankUserRefs(bank);
        return {
            id: entity.id ?? entity.Id ?? bankId,
            title: entity.title ?? entity.Title ?? "",
            qrCode: entity.qrCode ?? entity.QrCode ?? "",
            version: extractVersion(entity) ?? extractVersion(bank) ?? 0,
            exams: entity.subExams ?? entity.exams ?? entity.Exams ?? [],
            childBanks:
                entity.childExamBanks ??
                entity.childBanks ??
                entity.ChildBanks ??
                [],
            users: rawUsers.length > 0 ? rawUsers : fallbackRefs.rawUsers,
            userIds: rawIds.length > 0 ? rawIds : fallbackRefs.rawIds,
        };
    }, []);

    const fetchBankFromList = useCallback(async (bankId) => {
        if (!bankId) return null;
        const response = await axios.post(
            `${config.apiBaseUrl}/api/ExamBank/GetList`,
            {
                Page: 1,
                PageSize: 1,
                Filter: `id=${bankId}`,
                OrderBy: "",
            },
            {
                headers: {
                    "Content-Type": "application/json",
                    HeaderKey: config.HeaderKey,
                },
                withCredentials: true,
            }
        );
        const payload = response.data?.data;
        const rows = Array.isArray(payload)
            ? payload
            : Array.isArray(payload?.data)
                ? payload.data
                : [];
        return mapBankPayload(rows[0], bankId);
    }, [mapBankPayload]);

    const fetchBankById = useCallback(async (bankId = initialBank?.id) => {
        if (!bankId) return null;

        let mapped = null;
        try {
            const response = await axios.get(
                `${config.apiBaseUrl}/api/ExamBank/GetExamBank?id=${bankId}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        HeaderKey: config.HeaderKey,
                    },
                    withCredentials: true,
                }
            );
            mapped = mapBankPayload(response.data?.data ?? response.data, bankId);
        } catch {
            mapped = null;
        }

        // GetExamBank often omits users; list endpoint usually includes them.
        if (!mapped || !bankHasUserRefs(mapped)) {
            try {
                const fromList = await fetchBankFromList(bankId);
                if (fromList) {
                    mapped = mapped
                        ? {
                            ...mapped,
                            ...fromList,
                            version: fromList.version ?? mapped.version,
                            users:
                                fromList.users?.length > 0
                                    ? fromList.users
                                    : mapped.users,
                            userIds:
                                fromList.userIds?.length > 0
                                    ? fromList.userIds
                                    : mapped.userIds,
                        }
                        : fromList;
                }
            } catch {
                // keep mapped detail if we have it
            }
        }

        return mapped;
    }, [initialBank?.id, mapBankPayload, fetchBankFromList]);

    const applyBankToForm = useCallback(async (bank) => {
        if (!bank) return;

        setTitle(bank.title || "");
        setQrCode(bank.qrCode ?? bank.QrCode ?? "");
        setVersion(extractVersion(bank) ?? bank.version ?? 0);
        setQrCodeError("");

        if (Array.isArray(bank.exams) && bank.exams.length > 0) {
            const initExams = sortByOrder(
                bank.exams.map((ex, index) => {
                    const normalized = normalizeBankItemOrder(ex, index);
                    const examId = normalized.id;
                    const examTitle = ex?.title || ex?.name || examId;
                    return {
                        id: examId,
                        title: examTitle,
                        key: examId,
                        order: normalized.order,
                        qCount: ex?.examQuestions?.length || 0,
                    };
                })
            );
            setSelectedExams(initExams);
        } else {
            setSelectedExams([]);
        }

        if (Array.isArray(bank.childBanks) && bank.childBanks.length > 0) {
            const initChild = sortByOrder(
                bank.childBanks
                    .filter((cb) => !(initialBank?.id && (cb?.id || cb) === initialBank.id))
                    .map((cb, index) => {
                        const normalized = normalizeBankItemOrder(cb, index);
                        const bankId = normalized.id;
                        const bankTitle = cb?.title || cb?.name || bankId;
                        return {
                            id: bankId,
                            title: bankTitle,
                            key: bankId,
                            order: normalized.order,
                            examsCount: cb?.exams?.length || 0,
                        };
                    })
            );
            setSelectedChildBanks(initChild);
        } else {
            setSelectedChildBanks([]);
        }

        const resolvedUsers = await resolveBankUsers(bank);
        setSelectedUsers(resolvedUsers);

        setIsOutdated(false);
    }, [initialBank?.id]);

    // Initial data load
    useEffect(() => {
        const initializeData = async () => {
            // Fetch initial pages for both tables
            await Promise.all([
                fetchExams(examsPagination.current, examsPagination.pageSize, examsSearchFilters.title),
                fetchBanks(banksPagination.current, banksPagination.pageSize, banksSearchFilters.title)
            ]);

            // Initialize form fields if editing existing bank
            if (initialBank) {
                try {
                    const fresh = await fetchBankById(initialBank.id);
                    // Merge so navigation-state users/userIds are not dropped if GetExamBank omits them.
                    const merged = fresh
                        ? {
                            ...initialBank,
                            ...fresh,
                            users:
                                (fresh.users && fresh.users.length > 0
                                    ? fresh.users
                                    : initialBank.users) || [],
                            userIds:
                                (fresh.userIds && fresh.userIds.length > 0
                                    ? fresh.userIds
                                    : initialBank.userIds) || [],
                        }
                        : initialBank;
                    await applyBankToForm(merged);
                } catch {
                    await applyBankToForm(initialBank);
                }
            } else if (parentBank) {
                const bankId = parentBank.id || parentBank.Id;
                setSelectedParentBanks([{
                    id: bankId,
                    title: parentBank.title || parentBank.Title || bankId,
                    key: bankId,
                }]);
            }
        };

        initializeData();
    }, [initialBank, parentBank, isSelf, fetchExams, fetchBanks, fetchBankById, applyBankToForm, examsPagination.current, examsPagination.pageSize, banksPagination.current, banksPagination.pageSize]);

    // Handle exams table pagination change
    const handleExamsPaginationChange = (page, pageSize) => {
        setExamsPagination({ current: page, pageSize });
        fetchExams(page, pageSize, examsSearchFilters.title);
    };

    // Handle banks table pagination change
    const handleBanksPaginationChange = (page, pageSize) => {
        setBanksPagination({ current: page, pageSize });
        fetchBanks(page, pageSize, banksSearchFilters.title);
    };

    const examsRowSelection = {
        selectedRowKeys: selectedExamIds,
        onChange: (keys) => {
            if (keys.length === 0) {
                updateSelectedExams([]);
                return;
            }

            const existingMap = new Map(selectedExams.map((e) => [e.id, e]));
            const next = [];
            let nextOrder = getNextOrder(selectedExams);

            keys.forEach((id) => {
                if (existingMap.has(id)) {
                    next.push(existingMap.get(id));
                } else {
                    const found = allExams.find((e) => e.id === id);
                    if (found) {
                        next.push({ ...found, order: nextOrder });
                        nextOrder += 1;
                    }
                }
            });
            updateSelectedExams(next);
        },
        preserveSelectedRowKeys: true,
    };

    const childBanksRowSelection = {
        selectedRowKeys: selectedChildBankIds,
        onChange: (keys) => {
            if (keys.length === 0) {
                updateSelectedChildBanks([]);
                return;
            }

            const existingMap = new Map(selectedChildBanks.map((b) => [b.id, b]));
            const next = [];
            let nextOrder = getNextOrder(selectedChildBanks);

            for (const id of keys) {
                if (isSelf(id)) {
                    toast.warn("یک بانک نمی‌تواند فرزند خودش باشد.");
                    continue;
                }
                if (existingMap.has(id)) {
                    next.push(existingMap.get(id));
                } else {
                    const found = allBanks.find((b) => b.id === id);
                    if (found) {
                        next.push({ ...found, order: nextOrder });
                        nextOrder += 1;
                    }
                }
            }
            updateSelectedChildBanks(next);
        },
        preserveSelectedRowKeys: true,
    };

    const parentBanksRowSelection = {
        selectedRowKeys: selectedParentBankIds,
        onChange: (keys) => {
            const existingMap = new Map(selectedParentBanks.map((b) => [b.id, b]));
            const next = [];
            for (const id of keys) {
                if (isSelf(id)) {
                    toast.warn("یک بانک نمی‌تواند والد خودش باشد.");
                    continue;
                }
                if (existingMap.has(id)) next.push(existingMap.get(id));
                else {
                    const found = allBanks.find((b) => b.id === id);
                    if (found) next.push({ ...found });
                }
            }
            setSelectedParentBanks(next);
        },
        preserveSelectedRowKeys: true,
    };

    const examColumns = [
        { title: "تعداد سوالات", dataIndex: "qCount", key: "qCount", width: 250, align: "center", className: "font-custom text-center" },
        {
            title: "عنوان آزمون",
            dataIndex: "title",
            key: "title",
            align: "right",
            className: "font-custom text-right",
            filteredValue: examsSearchFilters.title ? [examsSearchFilters.title] : null,
            filterDropdown: () => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="جستجوی عنوان"
                        value={examsSearchFilters.title}
                        onChange={(e) => handleExamsSearchChange(e, "title")}
                        onPressEnter={() => fetchExamsWithFilters()}
                        suffix={
                            <ClearSuffix
                                onClick={() => {
                                    setExamsSearchFilters((prev) => ({ ...prev, title: "" }));
                                    fetchExamsWithFilters({ title: "" });
                                }}
                                style={{ visibility: examsSearchFilters.title ? "visible" : "hidden" }}
                            />
                        }
                        style={{ marginBottom: 8, display: "flex", direction: "rtl" }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => fetchExamsWithFilters()}
                            icon={<SearchOutlined size={16} />}
                            size="small"
                            className="font-custom"
                        >
                            جستجو
                        </Button>
                    </Space>
                </div>
            ),
        },
    ];

    const bankColumns = [
        { title: "تعداد آزمون", dataIndex: "examsCount", key: "examsCount", width: 240, align: "center", className: "font-custom text-center" },
        {
            title: "عنوان بانک",
            dataIndex: "title",
            key: "title",
            align: "right",
            className: "font-custom text-right",
            filteredValue: banksSearchFilters.title ? [banksSearchFilters.title] : null,
            filterDropdown: () => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="جستجوی عنوان"
                        value={banksSearchFilters.title}
                        onChange={(e) => handleBanksSearchChange(e, "title")}
                        onPressEnter={() => fetchBanksWithFilters()}
                        suffix={
                            <ClearSuffix
                                onClick={() => {
                                    setBanksSearchFilters((prev) => ({ ...prev, title: "" }));
                                    fetchBanksWithFilters({ title: "" });
                                }}
                                style={{ visibility: banksSearchFilters.title ? "visible" : "hidden" }}
                            />
                        }
                        style={{ marginBottom: 8, display: "flex", direction: "rtl" }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => fetchBanksWithFilters()}
                            icon={<SearchOutlined size={16} />}
                            size="small"
                            className="font-custom"
                        >
                            جستجو
                        </Button>
                    </Space>
                </div>
            ),
        },
    ];

    const selectedListColumns = (onRemove, onOrderChange) => [
        {
            title: "حذف",
            key: "remove",
            className: "font-custom",
            render: (_, record) => (
                <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => onRemove(record.id)}
                    size="small"
                />
            ),
            width: 90,
            align: "center",
        },
        {
            title: "ترتیب",
            dataIndex: "order",
            key: "order",
            align: "center",
            className: "font-custom text-center",
            width: 100,
            render: (_, record) => (
                <input
                    type="number"
                    min={0}
                    className="border rounded text-center px-2 py-1 w-20"
                    value={record.order ?? 0}
                    onChange={(e) => {
                        const value = parseInt(e.target.value, 10);
                        onOrderChange(record.id, Number.isNaN(value) ? 0 : value);
                    }}
                />
            ),
        },
        {
            title: "عنوان",
            dataIndex: "title",
            key: "title",
            align: "right",
            className: "font-custom text-right",
        },
    ];

    const handleVersionConflictRefresh = async () => {
        setIsRefreshing(true);
        try {
            // fetchBankById falls back to GetList when GetExamBank omits users.
            const bank = await fetchBankById(initialBank?.id);
            if (!bank) throw new Error("bank not found");
            await applyBankToForm(bank);
            setVersionConflict({ open: false, message: "" });
            toast.success(
                <p dir="rtl" className="font-custom">اطلاعات بانک آزمون بازخوانی شد</p>
            );
        } catch {
            toast.error(<p dir="rtl" className="font-custom">خطا در بازخوانی بانک آزمون</p>);
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleVersionConflictCancel = () => {
        setVersionConflict({ open: false, message: "" });
        navigate(-1);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isOutdated) {
            setVersionConflict((prev) => ({ ...prev, open: true }));
            return;
        }

        if (!title.trim()) {
            toast.error("عنوان الزامی است.");
            return;
        }

        if (isSelf(selectedChildBankIds.find(Boolean)) || isSelf(selectedParentBankIds.find(Boolean))) {
            toast.error("انتخاب خودِ بانک به عنوان فرزند/والد مجاز نیست.");
            return;
        }

        const examsPayload = toExamBankItemDtos(selectedExams);
        const childBanksPayload = toExamBankItemDtos(selectedChildBanks);

        const examValidationError = validateOrderedItems(examsPayload, {
            idLabel: "آزمون‌ها",
            orderLabel: "آزمون‌ها",
        });
        if (examValidationError) {
            toast.error(examValidationError);
            return;
        }

        const childBankValidationError = validateOrderedItems(childBanksPayload, {
            idLabel: "بانک‌های فرزند",
            orderLabel: "بانک‌های فرزند",
        });
        if (childBankValidationError) {
            toast.error(childBankValidationError);
            return;
        }

        setIsLoading(true);
        setQrCodeError("");

        const basePayload = withOptionalQrCode({
            title: title.trim(),
            exams: examsPayload,
            childBanks: childBanksPayload,
        }, qrCode);

        try {
            if (initialBank?.id) {
                const payload = {
                    ...basePayload,
                    id: initialBank.id,
                    version: version ?? 0,
                    userIds: selectedUsers.map((u) => u.userID),
                };

                const response = await axios.post(`${config.apiBaseUrl}/api/exambank/UpdateExamBank`, payload, {
                    headers: { "Content-Type": "application/json", HeaderKey: config.HeaderKey },
                    withCredentials: true,
                });
                setQrCode(
                    response.data?.data?.qrCode ??
                    response.data?.qrCode ??
                    normalizeQrCode(qrCode)
                );
                const nextVersion = extractVersion(response.data);
                if (nextVersion != null) setVersion(nextVersion);
                toast.success("بانک آزمون با موفقیت به‌روزرسانی شد.");
            } else {
                const payload = {
                    ...basePayload,
                    userIds: selectedUsers.map((u) => u.userID),
                };

                const response = await axios.post(`${config.apiBaseUrl}/api/exambank/CreateExamBank`, payload, {
                    headers: { "Content-Type": "application/json", HeaderKey: config.HeaderKey },
                    withCredentials: true,
                });
                setQrCode(
                    response.data?.data?.qrCode ??
                    response.data?.qrCode ??
                    normalizeQrCode(qrCode)
                );
                toast.success(<p dir="rtl" className="font-custom">بانک آزمون با موفقیت ایجاد شد.</p>);
            }

            navigate(-1);
        } catch (err) {
            if (isVersionConflict(err)) {
                setIsOutdated(true);
                setVersionConflict({
                    open: true,
                    message: getVersionConflictMessage(err),
                });
            } else {
                const errorMessage = getApiErrorMessage(err, "خطا در ثبت اطلاعات");
                if (isQrCodeConflict(err)) {
                    setQrCodeError(errorMessage);
                }
                toast.error(<p dir="rtl" className="font-custom">{errorMessage}</p>);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const removeExam = (id) => updateSelectedExams(selectedExams.filter((x) => x.id !== id));
    const removeChildBank = (id) => updateSelectedChildBanks(selectedChildBanks.filter((x) => x.id !== id));

    const updateExamOrder = (id, order) => {
        updateSelectedExams(
            selectedExams.map((item) => (item.id === id ? { ...item, order } : item))
        );
    };

    const updateChildBankOrder = (id, order) => {
        updateSelectedChildBanks(
            selectedChildBanks.map((item) => (item.id === id ? { ...item, order } : item))
        );
    };
    const removeParentBank = (id) => setSelectedParentBanks((prev) => prev.filter((x) => x.id !== id));

    return (
        <form className="max-w-6xl mx-auto p-6 bg-white rounded shadow" onSubmit={handleSubmit}>
            <h2 className="text-xl text-center font-custom font-bold mb-6">
                {initialBank ? "ویرایش بانک آزمون" : "ایجاد بانک آزمون"}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <QrCodeField
                    value={qrCode}
                    error={qrCodeError}
                    onChange={(value) => {
                        setQrCode(value);
                        setQrCodeError("");
                    }}
                />

                <div>
                    <label className="block text-right font-custom mb-1 font-medium">عنوان</label>
                    <input
                        className="border rounded px-3 py-2 w-full font-custom"
                        dir="rtl"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                    />
                </div>
            </div>

            <div className="mb-10">
                <div className="flex items-center my-4">
                    <div className="flex-grow border-t border-gray-300" />
                    <h3 className="mx-4 text-lg font-bold text-gray-600 whitespace-nowrap font-custom">انتخاب آزمون‌ها</h3>
                    <div className="flex-grow border-t border-gray-300" />
                </div>
                <Table
                    rowSelection={examsRowSelection}
                    columns={examColumns}
                    dataSource={allExams}
                    rowKey="id"
                    pagination={{
                        current: examsPagination.current,
                        pageSize: examsPagination.pageSize,
                        total: examTotal,
                        onChange: handleExamsPaginationChange,
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} از ${total} آزمون`,
                    }}
                />
            </div>

            <div className="mb-10">
                <h3 className="text-lg font-semibold mb-2 text-right font-custom">آزمون‌های انتخاب‌شده</h3>
                <Table
                    columns={selectedListColumns(removeExam, updateExamOrder)}
                    dataSource={selectedExams}
                    rowKey="id"
                    pagination={{
                        pageSize: 6,
                        showTotal: (total, range) => `${range[0]}-${range[1]} از ${total} آزمون`,
                    }}
                />
                <div className="mt-3 flex gap-2">
                    <Button
                        className="font-custom"
                        onClick={() => updateSelectedExams([...selectedExams])}
                    >
                        مرتب‌سازی بر اساس ترتیب
                    </Button>
                    <Button className="font-custom" danger onClick={() => updateSelectedExams([])}>
                        حذف همه آزمون‌ها
                    </Button>
                </div>
            </div>

            <div className="mb-10">
                <div className="flex items-center my-4">
                    <div className="flex-grow border-t border-gray-300" />
                    <h3 className="mx-4 text-lg font-bold text-gray-600 whitespace-nowrap font-custom">انتخاب بانک‌های فرزند</h3>
                    <div className="flex-grow border-t border-gray-300" />
                </div>
                <Table
                    rowSelection={childBanksRowSelection}
                    columns={bankColumns}
                    dataSource={allBanks}
                    rowKey="id"
                    pagination={{
                        current: banksPagination.current,
                        pageSize: banksPagination.pageSize,
                        total: bankTotal,
                        onChange: handleBanksPaginationChange,
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} از ${total} بانک`,
                    }}
                />
            </div>

            <div className="mb-10">
                <h3 className="text-lg font-semibold mb-2 text-right font-custom">بانک‌های فرزند انتخاب‌شده</h3>
                <Table
                    columns={selectedListColumns(removeChildBank, updateChildBankOrder)}
                    dataSource={selectedChildBanks}
                    rowKey="id"
                    pagination={{
                        pageSize: 6,
                        showTotal: (total, range) => `${range[0]}-${range[1]} از ${total} بانک`,
                    }}
                />
                <div className="mt-3 flex gap-2">
                    <Button
                        className="font-custom"
                        onClick={() => updateSelectedChildBanks([...selectedChildBanks])}
                    >
                        مرتب‌سازی بر اساس ترتیب
                    </Button>
                    <Button className="font-custom" danger onClick={() => updateSelectedChildBanks([])}>
                        حذف همه بانک‌های فرزند
                    </Button>
                </div>
            </div>

            {/* <div className="mb-10">
        <div className="flex items-center my-4">
          <div className="flex-grow border-t border-gray-300" />
          <h3 className="mx-4 text-lg font-bold text-gray-600 whitespace-nowrap font-custom">انتخاب بانک‌های والد (اختیاری)</h3>
          <div className="flex-grow border-t border-gray-300" />
        </div>
        <Table
          rowSelection={parentBanksRowSelection}
          columns={bankColumns}
          dataSource={allBanks}
          rowKey="id"
          pagination={{ pageSize: 6, total: bankTotal }}
        />
      </div>

      <div className="mb-10">
        <h3 className="text-lg font-semibold mb-2 text-right font-custom">بانک‌های والد انتخاب‌شده</h3>
        <Table
          columns={selectedListColumns(removeParentBank)}
          dataSource={selectedParentBanks}
          rowKey="id"
          pagination={{ pageSize: 6, total: selectedParentBankIds.length }}
        />
        <div className="mt-3 flex gap-2">
          <Button className="font-custom" danger onClick={() => setSelectedParentBanks([])}>
            حذف همه بانک‌های والد
          </Button>
        </div>
      </div> */}
            <div className="mb-8">
                <SelectUsersMiniTable
                    value={selectedUsers}
                    onChange={setSelectedUsers}
                    apiUrl="/api/users/getuserslist"
                    pageSize={12}
                />
            </div>
            <button
                type="submit"
                disabled={isLoading || isOutdated}
                className="w-full flex justify-center items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg text-sm px-4 py-2.5 transition-colors duration-300 disabled:opacity-70"
            >
                {isLoading ? <FaSpinner className="animate-spin" /> : initialBank ? "به‌روزرسانی بانک آزمون" : "ایجاد بانک آزمون"}
            </button>
            <VersionConflictModal
                open={versionConflict.open}
                message={versionConflict.message}
                refreshing={isRefreshing}
                onRefresh={handleVersionConflictRefresh}
                onCancel={handleVersionConflictCancel}
            />

            <ToastContainer
                position="bottom-center"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                className={"font-custom text-center rtl"}
                pauseOnHover
                theme="dark"
                transition={Bounce}
            />
        </form>
    );
};

export default ExamBankForm;
